import { useState, useCallback, useRef, useEffect } from 'react';
import { SimWorld, createSimWorld, CreateSimWorldOptions } from '../../simulation/types/SimWorld';
import { GameMap } from '../../simulation/types/GameMap';
import { simulateDay } from '../../simulation/core/DailyCycle';
import { generateMap } from '../../simulation/map/MapGenerator';
import { placeNPCOnMap } from '../../simulation/map/PositionUtils';
import { createSimNPC } from '../../simulation/types/SimNPC';
import { generateUniqueName } from '../../simulation/utils/NameGenerator';
import { getAvailableCards, CardPoolConfig } from '../../simulation/utils/CardFilter';
import type { ExtendedCardData } from '../../utils/cardManager';
import type { CardWithId } from '../../types/card';
import type { GroupModeConfig, GroupConfig, GroupStats, GroupId } from '../../simulation/types/Group';

export interface SimulationConfig {
  initialNPCCount: number;
  mapWidth: number;
  mapHeight: number;
  /** 除外するカードIDのセット */
  excludedCardIds?: Set<string>;
  /** カードプール設定（拡張用） */
  cardPoolConfig?: CardPoolConfig;
  /** グループモード設定 */
  groupModeConfig?: GroupModeConfig;
}

export interface SimulationControls {
  world: SimWorld;
  map: GameMap;
  isPlaying: boolean;
  speed: number; // 1 = 1日/秒, 2 = 2日/秒, etc.
  config: SimulationConfig;

  play: () => void;
  pause: () => void;
  step: () => void; // 1日進める
  reset: () => void;
  resetWithConfig: (newConfig: Partial<SimulationConfig>) => void;
  setSpeed: (speed: number) => void;
}

/**
 * ランダムデッキ生成
 * @param allCards 全カードリスト
 * @param cardPoolConfig カードプール設定（除外カード等）
 */
function createRandomDeck(
  allCards: ExtendedCardData[],
  cardPoolConfig?: CardPoolConfig
): CardWithId[] {
  const availableCards = getAvailableCards(allCards, cardPoolConfig);
  if (availableCards.length === 0) {
    console.warn('利用可能なカードがありません。全カードを使用します。');
    return createRandomDeckFromCards(allCards);
  }
  return createRandomDeckFromCards(availableCards);
}

/**
 * カードリストからランダムデッキを生成（内部関数）
 */
function createRandomDeckFromCards(cards: ExtendedCardData[]): CardWithId[] {
  const deck: CardWithId[] = [];
  for (let i = 0; i < 40; i++) {
    const card = cards[Math.floor(Math.random() * cards.length)];
    const cardCopy: CardWithId = { ...card, uniqueId: `${card.id}_${i}_${Math.random()}` };
    deck.push(cardCopy);
  }
  return deck;
}

/**
 * 初期ワールド作成
 */
function createInitialWorld(
  config: SimulationConfig,
  allCards: ExtendedCardData[],
  map: GameMap
): SimWorld {
  const existingNames = new Set<string>();
  const npcs = [];

  // グループモードの場合
  if (config.groupModeConfig?.enabled && config.groupModeConfig.groups.length > 0) {
    return createInitialWorldGroupMode(config, allCards, map, existingNames);
  }

  // 通常モード
  // CardPoolConfigを構築
  const cardPoolConfig: CardPoolConfig | undefined = config.excludedCardIds
    ? { excludedCardIds: config.excludedCardIds, ...config.cardPoolConfig }
    : config.cardPoolConfig;

  for (let i = 0; i < config.initialNPCCount; i++) {
    // ランダムでユニークな名前を生成
    const name = generateUniqueName(existingNames);
    existingNames.add(name);

    const npc = createSimNPC(name, createRandomDeck(allCards, cardPoolConfig));

    // ランダムな初期位置
    const randomX = Math.floor(Math.random() * map.width);
    const randomY = Math.floor(Math.random() * map.height);
    npc.position = { x: randomX, y: randomY };
    placeNPCOnMap(npc, map);

    npcs.push(npc);
  }

  // cardPoolConfigをワールドに保持（他のシステムが参照できるように）
  return createSimWorld(npcs, { cardPoolConfig });
}

/**
 * グループモード用初期ワールド作成
 */
function createInitialWorldGroupMode(
  config: SimulationConfig,
  allCards: ExtendedCardData[],
  map: GameMap,
  existingNames: Set<string>
): SimWorld {
  const npcs = [];
  const groups = config.groupModeConfig!.groups;

  for (const group of groups) {
    // グループのカードプール設定
    const groupCardPoolConfig: CardPoolConfig = {
      ...group.cardPoolConfig,
      excludedCardIds: config.excludedCardIds,
    };

    // 各グループにinitialNpcCount人のNPCを作成
    for (let i = 0; i < group.initialNpcCount; i++) {
      const name = generateUniqueName(existingNames);
      existingNames.add(name);

      const npc = createSimNPC(name, createRandomDeck(allCards, groupCardPoolConfig), {
        group: group.id,
      });

      // ランダムな初期位置
      const randomX = Math.floor(Math.random() * map.width);
      const randomY = Math.floor(Math.random() * map.height);
      npc.position = { x: randomX, y: randomY };
      placeNPCOnMap(npc, map);

      npcs.push(npc);
    }
  }

  return createSimWorld(npcs, {
    groupModeConfig: config.groupModeConfig,
  });
}

/**
 * NPCを深くコピー（ミューテーション防止）
 */
function deepCloneNPC(npc: ReturnType<typeof createSimNPC>): ReturnType<typeof createSimNPC> {
  // relationships Mapの深いコピー（各Relationshipオブジェクトもクローン）
  const clonedRelationships = new Map<string, ReturnType<typeof createSimNPC>['relationships'] extends Map<string, infer V> ? V : never>();
  npc.relationships.forEach((rel, key) => {
    clonedRelationships.set(key, { ...rel });
  });

  // cardStats Mapの深いコピー（各CardStatオブジェクトもクローン）
  const clonedCardStats = new Map<string, ReturnType<typeof createSimNPC>['cardStats'] extends Map<string, infer V> ? V : never>();
  npc.cardStats.forEach((stat, key) => {
    clonedCardStats.set(key, { ...stat });
  });

  return {
    ...npc,
    deck: [...npc.deck],
    relationships: clonedRelationships,
    family: [...npc.family],
    position: { ...npc.position },
    cardStats: clonedCardStats,
    recentOpponents: [...npc.recentOpponents],
  };
}

/**
 * Worldを深くコピー
 */
function deepCloneWorld(world: SimWorld): SimWorld {
  // groupStatsの深いコピー
  let clonedGroupStats: Map<GroupId, GroupStats> | undefined;
  if (world.groupStats) {
    clonedGroupStats = new Map();
    world.groupStats.forEach((stats, groupId) => {
      clonedGroupStats!.set(groupId, {
        ...stats,
        winsVsGroup: new Map(stats.winsVsGroup),
        lossesVsGroup: new Map(stats.lossesVsGroup),
      });
    });
  }

  return {
    ...world,
    npcs: world.npcs.map(deepCloneNPC),
    logs: [...world.logs],
    dailySnapshots: [...world.dailySnapshots],
    groupStats: clonedGroupStats,
  };
}

/**
 * マップを深くコピー（タイルのnpcs配列もクローン）
 */
function deepCloneMap(map: GameMap): GameMap {
  return {
    ...map,
    tiles: map.tiles.map(row =>
      row.map(tile => ({
        ...tile,
        npcs: [...tile.npcs],
      }))
    ),
  };
}

/**
 * シミュレーション制御フック
 */
export function useSimulation(
  allCards: ExtendedCardData[],
  initialConfig: SimulationConfig
): SimulationControls {
  // 初期マップ生成（不変）
  const [initialMap] = useState<GameMap>(() => generateMap());

  // 現在の設定
  const [config, setConfig] = useState<SimulationConfig>(initialConfig);

  // 状態管理
  const [world, setWorld] = useState<SimWorld>(() =>
    createInitialWorld(initialConfig, allCards, initialMap)
  );
  const [map, setMap] = useState<GameMap>(initialMap);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  // ワールドとマップの最新値を保持するref（シミュレーション用）
  const worldRef = useRef<SimWorld>(world);
  const mapRef = useRef<GameMap>(map);
  // StrictModeでの重複実行を防ぐためのフラグ
  const lastProcessedDayRef = useRef(-1);

  // 1日進める（refベースでシミュレーション、その後stateに反映）
  const step = useCallback(() => {
    const currentWorld = worldRef.current;
    const currentMap = mapRef.current;

    // 同じ日を2回処理しない（StrictMode対策）
    if (currentWorld.day === lastProcessedDayRef.current) {
      return;
    }
    lastProcessedDayRef.current = currentWorld.day;

    // ワールドとマップを深くコピー
    const nextWorld = deepCloneWorld(currentWorld);
    const nextMap = deepCloneMap(currentMap);

    // シミュレーション実行
    simulateDay(nextWorld, allCards, nextMap);

    // refを更新
    worldRef.current = nextWorld;
    mapRef.current = nextMap;

    // React stateに反映
    setWorld(nextWorld);
    setMap(nextMap);
  }, [allCards]);

  // 再生
  const play = useCallback(() => {
    setIsPlaying(true);
  }, []);

  // 一時停止
  const pause = useCallback(() => {
    setIsPlaying(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // リセット（現在の設定で）
  const reset = useCallback(() => {
    pause();
    lastProcessedDayRef.current = -1;
    const newMap = generateMap();
    const newWorld = createInitialWorld(config, allCards, newMap);
    worldRef.current = newWorld;
    mapRef.current = newMap;
    setWorld(newWorld);
    setMap(newMap);
  }, [config, allCards, pause]);

  // 新しい設定でリセット
  const resetWithConfig = useCallback((newConfig: Partial<SimulationConfig>) => {
    pause();
    lastProcessedDayRef.current = -1;
    const mergedConfig = { ...config, ...newConfig };
    setConfig(mergedConfig);
    const newMap = generateMap();
    const newWorld = createInitialWorld(mergedConfig, allCards, newMap);
    worldRef.current = newWorld;
    mapRef.current = newMap;
    setWorld(newWorld);
    setMap(newMap);
  }, [config, allCards, pause]);

  // 自動進行処理（StrictMode対応）
  useEffect(() => {
    // 既存のintervalをクリア
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        step();
      }, 1000 / speed);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPlaying, speed, step]);

  return {
    world,
    map,
    isPlaying,
    speed,
    config,
    play,
    pause,
    step,
    reset,
    resetWithConfig,
    setSpeed,
  };
}
